import { useState } from 'react';
import { PrinterIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/shared/modal';
import { PrintButton } from '@/components/shared/print-button';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Button } from '@/components/shared/shadcn-ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/shadcn-ui/select';

const FONT_SCALES = {
  small: 0.75,
  normal: 1,
  large: 1.25,
  'extra-large': 1.5,
} as const;

type FontSize = keyof typeof FONT_SCALES;

const FONT_SIZE_STORAGE_KEY = 'print-font-size';

const isFontSize = (value: unknown): value is FontSize =>
  typeof value === 'string' && value in FONT_SCALES;

const readStoredFontSize = (): FontSize => {
  try {
    const stored = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    return isFontSize(stored) ? stored : 'normal';
  } catch {
    return 'normal';
  }
};

const storeFontSize = (fontSize: FontSize) => {
  try {
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, fontSize);
  } catch {
    // Remembering the choice is a convenience; printing still works without it
  }
};

interface PrintReportButtonProps {
  disabled?: boolean;
  className?: string;
  onPrint: (fontScale: number) => void;
}

export const PrintReportButton = ({
  disabled,
  className,
  onPrint,
}: PrintReportButtonProps) => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fontSize, setFontSize] = useState<FontSize>(readStoredFontSize);

  const openModal = () => {
    setFontSize(readStoredFontSize());
    setIsModalOpen(true);
  };

  const handlePrint = () => {
    storeFontSize(fontSize);
    setIsModalOpen(false);
    onPrint(FONT_SCALES[fontSize]);
  };

  return (
    <>
      <PrintButton
        disabled={disabled}
        className={className}
        onClick={openModal}
      />

      <Modal
        isModalOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={t('reports.print.title')}
        description={t('reports.print.description')}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="print-font-size">
              {t('reports.print.font-size')}
            </Label>
            <Select
              value={fontSize}
              onValueChange={(value) => {
                if (isFontSize(value)) setFontSize(value);
              }}
            >
              <SelectTrigger id="print-font-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {(Object.keys(FONT_SCALES) as FontSize[]).map((size) => (
                    <SelectItem key={size} value={size}>
                      {t(`reports.print.sizes.${size}`)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handlePrint} className="flex gap-1.5">
              <span>{t('common.print')}</span>
              <PrinterIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
